/**
 * EncounterAnalysisSummary Component
 *
 * A balanced single-pane view of encounter analysis data.
 * Uses a 2-column grid with truncated text and "show more" affordances
 * so the provider can scan key info at a glance and expand for detail.
 */

import React, { useCallback, useState } from 'react';
import {
  Assignment as AssignmentIcon,
  Biotech as BiotechIcon,
  CheckCircleOutline as CheckIcon,
  Description as DescriptionIcon,
  ErrorOutline as ErrorIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FolderOpen as DataIcon,
  Groups as GroupsIcon,
  Help as HelpIcon,
  LocalHospital as ClinicalIcon,
  LocalPharmacy as MedsIcon,
  MedicalInformation as DiagnosisIcon,
  NewReleases as NewIcon,
  Place as PlaceIcon,
  Psychology as PsychIcon,
  Shield as ShieldIcon,
  TaskAlt as TaskIcon,
  TrendingDown as WorseIcon,
  TrendingFlat as StableIcon,
  TrendingUp as ImprovingIcon,
  Videocam as VideocamIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatSnakeCase = (str: string): string =>
  str
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const PROBLEM_STATUS_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; label: string }
> = {
  stable: { icon: StableIcon, label: 'Stable', color: 'success' },
  improving: { icon: ImprovingIcon, label: 'Improving', color: 'success' },
  not_at_goal: { icon: WarningIcon, label: 'Not at Goal', color: 'warning' },
  worsening: { icon: WorseIcon, label: 'Worsening', color: 'error' },
  new: { icon: NewIcon, label: 'New', color: 'info' },
  resolved: { icon: CheckIcon, label: 'Resolved', color: 'success' },
  unknown: { icon: HelpIcon, label: 'Unknown', color: 'default' },
};

const SAFETY_STATUS_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; label: string }
> = {
  no_concerns: { icon: CheckIcon, label: 'No Concerns', color: 'success' },
  concerns_present: { icon: ErrorIcon, label: 'Concerns Present', color: 'error' },
  not_assessed: { icon: HelpIcon, label: 'Not Assessed', color: 'default' },
  unknown: { icon: HelpIcon, label: 'Unknown', color: 'default' },
};

// ─── Reusable sub-components ────────────────────────────────────────────────

/** Section header with icon pill and title */
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
}> = ({ icon, title, badge }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: '7px',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', flex: 1 }}
      >
        {title}
      </Typography>
      {badge}
    </Box>
  );
};

/** Tile wrapper used inside the grid */
const Tile: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        p: 2,
        height: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Truncated text with "Show more / less" toggle.
 * Shows `maxLines` via CSS line-clamp, with a toggle to expand.
 */
const TruncatedText: React.FC<{
  text: string;
  maxLines?: number;
  variant?: 'body2' | 'caption';
}> = ({ text, maxLines = 3, variant = 'body2' }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Typography
        variant={variant}
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.55,
          fontSize: variant === 'caption' ? '0.78rem' : '0.83rem',
          ...(!expanded && {
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </Typography>
      <ButtonBase
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          color: theme.palette.primary.main,
          fontSize: '0.75rem',
          fontWeight: 600,
          mt: 0.25,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {expanded ? 'Show less' : 'Show more'}
      </ButtonBase>
    </Box>
  );
};

/**
 * List that shows the first `limit` items, with a "+N more" expand toggle.
 */
const TruncatedList: React.FC<{
  items: React.ReactNode[];
  limit?: number;
}> = ({ items, limit = 4 }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = items.length > limit;
  const visibleItems = expanded ? items : items.slice(0, limit);
  const hiddenCount = items.length - limit;

  return (
    <Box>
      <Stack spacing={0.5}>{visibleItems}</Stack>
      {hasOverflow && (
        <Collapse in={expanded} timeout={200}>
          {/* Already rendered in visibleItems when expanded */}
        </Collapse>
      )}
      {hasOverflow && (
        <ButtonBase
          onClick={() => setExpanded(prev => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            color: theme.palette.primary.main,
            fontSize: '0.75rem',
            fontWeight: 600,
            mt: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {expanded ? (
            <>
              <CollapseIcon sx={{ fontSize: 16 }} /> Show less
            </>
          ) : (
            <>
              <ExpandIcon sx={{ fontSize: 16 }} /> +{hiddenCount} more
            </>
          )}
        </ButtonBase>
      )}
    </Box>
  );
};

/** Compact bullet item */
const BulletItem: React.FC<{ text: string; dotColor?: string }> = ({ text, dotColor }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          bgcolor: dotColor ?? theme.palette.primary.main,
          mt: '7px',
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Box>
  );
};

/** Checkmark list item */
const CheckItem: React.FC<{ text: string; color?: string }> = ({ text, color }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <CheckIcon
        sx={{ fontSize: 14, color: color ?? theme.palette.success.main, mt: '3px', flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.45 }}>
        {text}
      </Typography>
    </Box>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

interface EncounterAnalysisSummaryProps {
  /** The encounter analysis data (rich JSON from backend) */
  encounterAnalysis: Record<string, any> | null | undefined;
}

export const EncounterAnalysisSummary: React.FC<EncounterAnalysisSummaryProps> = ({
  encounterAnalysis,
}) => {
  const theme = useTheme();

  if (!encounterAnalysis) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
          No encounter analysis available
        </Typography>
      </Paper>
    );
  }

  const {
    summary,
    canonical_service_type,
    session_type,
    participants,
    problems_addressed,
    treatment_modalities,
    risk_assessment,
    clinical_markers,
    data_reviewed,
    activities_performed,
    attestations,
    treatment_facts,
    encounter_mode,
    place_of_care,
    semantic_tags,
  } = encounterAnalysis as any;

  // Prefer canonical_service_type.name, fall back to session_type.canonical
  const serviceTypeName: string | null =
    canonical_service_type?.name || session_type?.canonical || null;
  const clinicTerms: string[] =
    session_type?.clinic_terms || [];

  const safetyConfig = risk_assessment?.safety_status
    ? SAFETY_STATUS_CONFIG[risk_assessment.safety_status] ?? SAFETY_STATUS_CONFIG.unknown
    : null;

  const hasClinicalMarkerData =
    clinical_markers &&
    Object.values(clinical_markers).some((v: any) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    );

  const hasTreatmentFacts =
    treatment_facts &&
    (treatment_facts.medications?.length > 0 ||
      treatment_facts.supplements?.length > 0 ||
      treatment_facts.procedures?.length > 0);

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* ═══ Header bar ═══ */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 0.5 }}>
          Encounter Analysis
        </Typography>
        {serviceTypeName && (
          <Chip
            icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
            label={serviceTypeName}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
          />
        )}
        {encounter_mode && (
          <Chip
            icon={<VideocamIcon sx={{ fontSize: 14 }} />}
            label={encounter_mode}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        )}
        {place_of_care && (
          <Chip
            icon={<PlaceIcon sx={{ fontSize: 14 }} />}
            label={formatSnakeCase(place_of_care)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        )}
        {clinicTerms.map((term: string, idx: number) => (
          <Chip
            key={idx}
            label={term}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              fontSize: '0.65rem',
              height: 22,
            }}
          />
        ))}
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ═══ Summary (full width, truncated, max-width for readability) ═══ */}
        {summary && (
          <Box>
            <SectionHeader icon={<DescriptionIcon sx={{ fontSize: 16 }} />} title="Summary" />
            <Box sx={{ pl: 4, maxWidth: 800 }}>
              <TruncatedText text={summary} maxLines={3} />
            </Box>
          </Box>
        )}

        {/* ═══ Problems + Risk Assessment + Data Reviewed ═══ */}
        {((problems_addressed && problems_addressed.length > 0) ||
          (risk_assessment && safetyConfig) ||
          (data_reviewed && data_reviewed.length > 0)) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
              gap: 2,
            }}
          >
            {/* Problems Addressed */}
            {problems_addressed && problems_addressed.length > 0 && (
              <Tile>
                <SectionHeader
                  icon={<DiagnosisIcon sx={{ fontSize: 16 }} />}
                  title="Problems Addressed"
                  badge={
                    <Chip
                      label={problems_addressed.length}
                      size="small"
                      sx={{
                        height: 18,
                        minWidth: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  }
                />
                <TruncatedList
                  limit={3}
                  items={problems_addressed.map((problem: any, idx: number) => {
                    const sc =
                      PROBLEM_STATUS_CONFIG[problem.status] ?? PROBLEM_STATUS_CONFIG.unknown;
                    const StatusIcon = sc.icon;
                    return (
                      <ProblemRow
                        key={idx}
                        name={problem.problem}
                        details={problem.details}
                        statusLabel={sc.label}
                        statusColor={sc.color}
                        StatusIcon={StatusIcon}
                      />
                    );
                  })}
                />
              </Tile>
            )}

            {/* Risk Assessment */}
            {risk_assessment && safetyConfig && (
              <Tile>
                <SectionHeader
                  icon={<ShieldIcon sx={{ fontSize: 16 }} />}
                  title="Risk Assessment"
                  badge={
                    <Chip
                      icon={<safetyConfig.icon sx={{ fontSize: 14 }} />}
                      label={safetyConfig.label}
                      size="small"
                      color={safetyConfig.color as any}
                      sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
                    />
                  }
                />
                {risk_assessment.details && (
                  <Box sx={{ pl: 4 }}>
                    <TruncatedText text={risk_assessment.details} maxLines={4} variant="caption" />
                  </Box>
                )}
              </Tile>
            )}

            {/* Data Reviewed */}
            {data_reviewed && data_reviewed.length > 0 && (
              <Tile>
                <SectionHeader icon={<DataIcon sx={{ fontSize: 16 }} />} title="Data Reviewed" />
                <Stack spacing={0.75} sx={{ pl: 4 }}>
                  {data_reviewed.map((d: any, idx: number) => (
                    <Box key={idx}>
                      <Chip
                        label={formatSnakeCase(d.type)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, mb: 0.25 }}
                      />
                      {d.details && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.35,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {d.details}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Tile>
            )}
          </Box>
        )}

        {/* ═══ Participants + Modalities + Activities ═══ */}
        {((participants && participants.length > 0) ||
          (treatment_modalities && treatment_modalities.length > 0) ||
          (activities_performed && activities_performed.length > 0)) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
              gap: 2,
            }}
          >
            {participants && participants.length > 0 && (
              <Tile>
                <SectionHeader icon={<GroupsIcon sx={{ fontSize: 16 }} />} title="Participants" />
                <Stack spacing={0.75} sx={{ pl: 4 }}>
                  {participants.map((p: any, idx: number) => (
                    <Box key={idx}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {p.role}{p.count && p.count > 1 ? ` (${p.count})` : ''}
                      </Typography>
                      {p.notes && (
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary, lineHeight: 1.35, display: 'block' }}
                        >
                          {p.notes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Tile>
            )}

            {treatment_modalities && treatment_modalities.length > 0 && (
              <Tile>
                <SectionHeader icon={<PsychIcon sx={{ fontSize: 16 }} />} title="Modalities" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 4 }}>
                  {treatment_modalities.map((m: string, idx: number) => (
                    <Chip
                      key={idx}
                      label={m}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        fontSize: '0.72rem',
                        height: 24,
                      }}
                    />
                  ))}
                </Box>
              </Tile>
            )}

            {activities_performed && activities_performed.length > 0 && (
              <Tile>
                <SectionHeader
                  icon={<TaskIcon sx={{ fontSize: 16 }} />}
                  title="Activities"
                  badge={
                    <Chip
                      label={activities_performed.length}
                      size="small"
                      sx={{
                        height: 18,
                        minWidth: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  }
                />
                <Box sx={{ pl: 4 }}>
                  <TruncatedList
                    limit={4}
                    items={activities_performed.map((a: string, idx: number) => (
                      <CheckItem key={idx} text={a} />
                    ))}
                  />
                </Box>
              </Tile>
            )}
          </Box>
        )}

        {/* ═══ Medications + Attestations + Clinical Markers ═══ */}
        {(hasTreatmentFacts ||
          (attestations && attestations.length > 0) ||
          hasClinicalMarkerData) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
              gap: 2,
            }}
          >
            {hasTreatmentFacts && (
              <Tile>
                <SectionHeader icon={<MedsIcon sx={{ fontSize: 16 }} />} title="Medications & Treatment" />
                <Box sx={{ pl: 4 }}>
                  <TruncatedList
                    limit={4}
                    items={[
                      ...(treatment_facts.medications ?? []).map((med: string, i: number) => (
                        <BulletItem key={`m-${i}`} text={med} dotColor={theme.palette.primary.main} />
                      )),
                      ...(treatment_facts.supplements ?? []).map((sup: string, i: number) => (
                        <BulletItem key={`s-${i}`} text={sup} dotColor={theme.palette.info.main} />
                      )),
                      ...(treatment_facts.procedures ?? []).map((proc: string, i: number) => (
                        <BulletItem key={`p-${i}`} text={proc} dotColor={theme.palette.warning.main} />
                      )),
                    ]}
                  />
                </Box>
              </Tile>
            )}

            {attestations && attestations.length > 0 && (
              <Tile>
                <SectionHeader icon={<ClinicalIcon sx={{ fontSize: 16 }} />} title="Attestations" />
                <Box sx={{ pl: 4 }}>
                  <TruncatedList
                    limit={3}
                    items={attestations.map((att: string, idx: number) => (
                      <CheckItem key={idx} text={att} color={theme.palette.primary.main} />
                    ))}
                  />
                </Box>
              </Tile>
            )}

            {hasClinicalMarkerData && (
              <Tile>
                <SectionHeader icon={<BiotechIcon sx={{ fontSize: 16 }} />} title="Clinical Markers" />
                <Stack spacing={0.5} sx={{ pl: 4 }}>
                  {Object.entries(clinical_markers).map(([key, value]: [string, any]) => {
                    if (Array.isArray(value) && value.length === 0) return null;
                    if (!value) return null;
                    return (
                      <Box key={key}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            textTransform: 'uppercase',
                            fontSize: '0.6rem',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {formatSnakeCase(key)}
                        </Typography>
                        {Array.isArray(value) ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {value.map((item: any, idx: number) => (
                              <Chip
                                key={idx}
                                label={typeof item === 'string' ? item : JSON.stringify(item)}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                            {String(value)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Tile>
            )}
          </Box>
        )}

      </Box>
    </Paper>
  );
};

// ─── Problem row (extracted for TruncatedList) ──────────────────────────────

const ProblemRow: React.FC<{
  name: string;
  details?: string;
  statusLabel: string;
  statusColor: string;
  StatusIcon: React.ElementType;
}> = ({ name, details, statusLabel, statusColor, StatusIcon }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <StatusIcon
          sx={{
            fontSize: 14,
            color:
              theme.palette[statusColor as 'success' | 'warning' | 'error' | 'info']?.main ??
              theme.palette.grey[500],
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1 }}>
          {name}
        </Typography>
        <Chip
          label={statusLabel}
          size="small"
          color={statusColor as any}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
        />
      </Box>
      {details && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            pl: 2.5,
            mt: 0.25,
          }}
        >
          {details}
        </Typography>
      )}
    </Box>
  );
};
